import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import logo1 from '../../../assets/icons/logott.png';
import { storeRestaurantSelection, hasValidRestaurantSelection } from '../utils/restaurantCheck';

const SPLASH_DELAY_MS = 1800;

const BUTTON_STYLES = {
  primary:
    'py-3.5 bg-white border-2 border-gray-800 text-gray-800 rounded-full shadow-md hover:shadow-lg hover:bg-gray-50 active:scale-[0.98] transition-shadow',
  skip: 'mt-2 text-center text-gray-500 text-lg pt-6 hover:text-gray-800 cursor-pointer transition-colors font-spartan-medium underline-offset-4 hover:underline',
};

const spring = { type: 'spring', stiffness: 260, damping: 24 };
const softSpring = { type: 'spring', stiffness: 120, damping: 18 };

export default function Opening() {
  const [phase, setPhase] = useState('intro'); // intro | actions
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const urlParams = new URLSearchParams(location.search);
  const urlRestaurant = urlParams.get('restaurant');
  const urlTable = urlParams.get('table');
  const hasQRParams = !!(urlRestaurant && urlTable);

  const revealActions = useCallback(() => {
    setPhase('actions');
  }, []);

  useEffect(() => {
    if (hasQRParams) {
      storeRestaurantSelection(urlRestaurant, urlTable);
      navigate(`/home?restaurant=${urlRestaurant}&table=${urlTable}`, { replace: true });
    }
  }, [hasQRParams, urlRestaurant, urlTable, navigate]);

  useEffect(() => {
    if (reduceMotion) {
      setPhase('actions');
      return undefined;
    }
    const timer = setTimeout(revealActions, SPLASH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reduceMotion, revealActions]);

  const preserveParams = useCallback(
    (path) => {
      if (hasQRParams) {
        return `${path}?restaurant=${urlRestaurant}&table=${urlTable}`;
      }
      return path;
    },
    [hasQRParams, urlRestaurant, urlTable],
  );

  const handleSkip = useCallback(() => {
    const dest = hasValidRestaurantSelection() ? '/home' : '/restaurant-selection';
    navigate(preserveParams(dest));
  }, [navigate, preserveParams]);

  const handleLogin = useCallback(
    () => navigate(preserveParams('/login')),
    [navigate, preserveParams],
  );
  const handleSignup = useCallback(
    () => navigate(preserveParams('/signup')),
    [navigate, preserveParams],
  );

  const buttons = [
    { text: 'Log In', onClick: handleLogin, style: BUTTON_STYLES.primary },
    { text: 'Sign Up', onClick: handleSignup, style: BUTTON_STYLES.primary },
    { text: 'Browse as Guest', onClick: handleSkip, style: BUTTON_STYLES.skip, isLink: true },
  ];

  const showActions = phase === 'actions';

  if (hasQRParams) return null;

  return (
    <motion.div
      className="relative w-full min-h-screen flex flex-col justify-center items-center font-spartan overflow-hidden bg-gradient-to-b from-white via-cyan-50 to-cyan-100 md:from-cyan-50 md:via-white md:to-cyan-100"
      onClick={!showActions ? revealActions : undefined}
      role="presentation"
    >
      {/* Ambient background orbs */}
      {!reduceMotion && (
        <>
          <motion.div
            className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-cyan-200/40 blur-3xl"
            animate={{ x: [0, 30, 0], y: [0, 20, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-teal-200/35 blur-3xl"
            animate={{ x: [0, -25, 0], y: [0, -15, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          />
          <motion.div
            className="pointer-events-none absolute top-1/3 right-1/4 h-48 w-48 rounded-full bg-white/60 blur-2xl"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      )}

      <motion.div
        className="relative z-10 flex flex-col items-center px-8"
        animate={
          showActions
            ? { y: reduceMotion ? 0 : -48, scale: reduceMotion ? 1 : 0.88 }
            : { y: 0, scale: 1 }
        }
        transition={reduceMotion ? { duration: 0.1 } : { ...softSpring, duration: 0.9 }}
      >
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, scale: 0.88, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={reduceMotion ? { duration: 0.1 } : { ...spring, delay: 0.1 }}
        >
          <motion.img
            src={logo1}
            alt="Table Tap"
            className="w-[280px] sm:w-[320px] md:w-[380px] drop-shadow-sm"
            loading="eager"
            fetchPriority="high"
            animate={
              !showActions && !reduceMotion
                ? {
                    scale: [1, 1.03, 1],
                    filter: ['brightness(1)', 'brightness(1.04)', 'brightness(1)'],
                  }
                : { scale: 1 }
            }
            transition={
              !showActions && !reduceMotion
                ? { duration: 2.4, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.2 }
            }
          />
        </motion.div>

        <AnimatePresence>
          {!showActions && !reduceMotion && (
            <motion.p
              key="tagline"
              className="mt-4 text-center text-sm tracking-[0.2em] uppercase text-gray-500 font-spartan-medium"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              Order · Pay · Enjoy
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Loading dots while intro plays */}
      <AnimatePresence>
        {!showActions && !reduceMotion && (
          <motion.div
            className="absolute bottom-16 flex items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ delay: 0.6 }}
            aria-hidden
          >
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="h-2 w-2 rounded-full bg-cyan-400/80"
                animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showActions && (
          <motion.div
            key="actions"
            className="relative z-10 mt-6 w-72 max-w-[85vw] flex flex-col gap-3.5 pb-10 font-spartan-medium"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: reduceMotion ? 0 : 0.1,
                  delayChildren: reduceMotion ? 0 : 0.05,
                },
              },
            }}
            aria-live="polite"
          >
            {buttons.map((btn) => (
              <motion.button
                key={btn.text}
                type="button"
                variants={{
                  hidden: { opacity: 0, y: 20, scale: 0.96 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: reduceMotion ? { duration: 0.1 } : spring,
                  },
                }}
                whileHover={reduceMotion || btn.isLink ? undefined : { scale: 1.02, y: -1 }}
                whileTap={reduceMotion ? undefined : { scale: 0.97 }}
                className={btn.style}
                onClick={btn.onClick}
              >
                {btn.text}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {!showActions && !reduceMotion && (
        <motion.p
          className="absolute bottom-8 text-xs text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ delay: 1 }}
        >
          Tap anywhere to continue
        </motion.p>
      )}
    </motion.div>
  );
}
